import { GridApi } from '@ag-grid-community/core'
import { WorkbenchRow } from '../models/workbench-row.model'
import { isMoney } from './renderers/renderers'

export interface RangeSum {
  /** Number of numeric cells summed. */
  count: number
  /** Totals keyed by currency ('' for plain numbers). */
  byCurrency: Record<string, number>
  /** Single column label when the range spans exactly one column. */
  columnLabel: string | null
}

/**
 * Sum the numeric / Money values in the current cell ranges. Used by the footer so a user can
 * range-select numeric cells and read their total, per currency.
 */
export function computeRangeSum(api: GridApi): RangeSum {
  const ranges = api.getCellRanges() ?? []
  const byCurrency: Record<string, number> = {}
  let count = 0
  const columnIds = new Set<string>()

  ranges.forEach(range => {
    const columns = range.columns ?? []
    const start = Math.min(
      range.startRow?.rowIndex ?? 0,
      range.endRow?.rowIndex ?? 0,
    )
    const end = Math.max(
      range.startRow?.rowIndex ?? 0,
      range.endRow?.rowIndex ?? 0,
    )
    for (let rowIndex = start; rowIndex <= end; rowIndex++) {
      const node = api.getDisplayedRowAtIndex(rowIndex)
      if (!node) continue
      const row = node.data as WorkbenchRow | undefined
      if (!row) continue
      columns.forEach(column => {
        columnIds.add(column.getColId())
        const value = api.getValue(column, node)
        if (isMoney(value)) {
          byCurrency[value.currency] =
            (byCurrency[value.currency] ?? 0) + value.value
          count++
        } else if (typeof value === 'number') {
          byCurrency[''] = (byCurrency[''] ?? 0) + value
          count++
        }
      })
    }
  })

  const singleColumn = columnIds.size === 1 ? [...columnIds][0] : null
  const columnLabel = singleColumn
    ? api.getColumnDef(singleColumn)?.headerName ?? null
    : null

  return { count, byCurrency, columnLabel }
}
